import Swiper, { Navigation, Thumbs, Scrollbar } from "swiper";

export default class ProductSliders {
  constructor(mainSliderSelector, thumbsSliderSelector, options = {}) {
    // templates
    this.templateMain = document.querySelector("template[data-all-media-main]");
    this.templateThumbs = document.querySelector(
      "template[data-all-media-thumbs]"
    );
    // selectors
    this.mainSliderSelector = mainSliderSelector;
    this.thumbsSliderSelector = thumbsSliderSelector;
    // DOM elements
    this.mainSlider = document.querySelector(this.mainSliderSelector);
    this.thumbsSlider = document.querySelector(this.thumbsSliderSelector);
    // Swiper instances
    this.mainSwiperInstance = null;
    this.thumbsSwiperInstance = null;
    // Custom options
    this.options = options;
    this.isExpanded = options.isExpanded || false;
    this.initialSlide =
      typeof options.initialSlide === "number" ? options.initialSlide : 0;
  }

  createThumbSlider(currentMediaSize = 0) {
    this.thumbsSwiperInstance = new Swiper(this.thumbsSlider, {
      modules: [Navigation, Thumbs],
      spaceBetween: 24,
      slidesPerView: 8,
      threshold: 8,
      direction: "vertical",
      loop: currentMediaSize >= 8 ? true : false,
      autoHeight: this.thumbsSlider.closest('#originalThumbsContainer') ? false : true,
    });
  }

  createMainSlider(currentMediaSize = 0) {
    if (!this.thumbsSwiperInstance) return;

    console.log(
      `Creating main slider for ${this.isExpanded ? "LARGE" : "SMALL"} slider`
    );
    console.log("Main slider element:", this.mainSlider);
    console.log("Touch settings will be:", {
      touchRatio: 1, // Using consistent value
      allowTouchMove: true,
      simulateTouch: true,
    });

    this.mainSwiperInstance = new Swiper(this.mainSlider, {
      modules: [Navigation, Thumbs, Scrollbar],
      initialSlide: this.initialSlide,
      watchSlidesProgress: true,
      watchOverflow: true,
      roundLengths: true,
      normalizeSlideIndex: true,
      // Match original codebase behavior for both states
      loop: true,
      on: {
        init: (swiper) => {
          console.log(
            `${this.isExpanded ? "LARGE" : "SMALL"} slider initialized:`,
            swiper
          );
          console.log("Touch enabled:", swiper.allowTouchMove);
          console.log("Slides count:", swiper.slides.length);
          setTimeout(() => {
            document
              .querySelector(".product-skeleton")
              ?.classList.add("hidden");
            document
              .querySelector(".product-page")
              ?.classList.remove("invisible", "h-0");
          }, 200);
        },
        transitionStart: (swiper) => {
          // Safe check for swiper element
          const swiperEl =
            swiper.$el && swiper.$el[0] ? swiper.$el[0] : swiper.el;
          if (swiperEl) {
            const videos = swiperEl.querySelectorAll("video");
            Array.prototype.forEach.call(videos, (video) => {
              video.pause();
              video.currentTime = 0;
            });
          }
        },
        transitionEnd: (swiper) => {
          const activeIndex = swiper.activeIndex;
          const swiperEl =
            swiper.$el && swiper.$el[0] ? swiper.$el[0] : swiper.el;
          if (swiperEl) {
            const activeSlide =
              swiperEl.getElementsByClassName("swiper-slide")[activeIndex];
            if (activeSlide) {
              const activeSlideVideo =
                activeSlide.getElementsByTagName("video")[0];
              if (activeSlideVideo) {
                activeSlideVideo.play();
              }
            }
          }

          // Update progress bar
          this.updateProgressBar(swiper);
        },
        slideChange: (swiper) => {
          // Update progress bar on slide change
          this.updateProgressBar(swiper);
        },
      },
      allowSlidePrev: true,
      allowSlideNext: true,
      centeredSlides: false, // Always left-align slides
      navigation: {
        nextEl: this.isExpanded
          ? "#largeThumbsContainer .swiper-button-next"
          : "#smallThumbsContainer .swiper-button-next, #originalThumbsContainer .swiper-button-next",
        prevEl: this.isExpanded
          ? "#largeThumbsContainer .swiper-button-prev"
          : "#smallThumbsContainer .swiper-button-prev, #originalThumbsContainer .swiper-button-next",
      },
      scrollbar: {
        el: this.isExpanded
          ? "[data-product-slider-scroll-bar-large]"
          : "[data-product-slider-scroll-bar-small]",
        draggable: true,
      },
      spaceBetween: 10,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
      slidesPerGroup: 1, // Always advance one slide at a time
      slidesPerGroupSkip: 0,
      breakpoints: {
        0: {
          slidesPerView: this.isExpanded ? 1 : "auto", // Small: auto width (173px), Large: 1 full
          spaceBetween: this.isExpanded ? 0 : 4, // Small: 4px per Figma, Large: 0
          centeredSlides: false,
          slidesPerGroup: 1,
        },
        648: {
          slidesPerView: this.isExpanded ? 1 : "auto", // Small: auto width, Large: 1
          spaceBetween: this.isExpanded ? 0 : 4,
          centeredSlides: false,
          slidesPerGroup: 1,
        },
        1024: {
          slidesPerView: this.isExpanded ? 1 : "auto", // Small: auto width, Large: 1
          spaceBetween: this.isExpanded ? 0 : 4,
          centeredSlides: false,
          slidesPerGroup: 1,
        },
      },
      mousewheel: this.isExpanded
        ? {
            // Large slider: normal swipe/scroll behavior
            forceToAxis: true,
            releaseOnEdges: true,
            sensitivity: 1,
          }
        : {
            // Small slider: strict one-by-one advancement
            forceToAxis: true,
            releaseOnEdges: true,
            sensitivity: 0.1, // Very low sensitivity for precise control
            thresholdDelta: 50, // Higher threshold to prevent accidental multi-slide
            thresholdTime: 300, // Shorter time window
          },
      // Touch/swipe settings - simplified for reliability
      touchRatio: 1,
      touchAngle: 45,
      simulateTouch: true,
      allowTouchMove: true,
      touchStartPreventDefault: true,
      touchMoveStopPropagation: true,
      resistanceRatio: 0.85,
      speed: this.isExpanded ? 300 : 250,
      thumbs: {
        swiper: this.thumbsSwiperInstance,
      },
    });
    return;
  }

  updateProgressBar(swiper) {
    const progressBarSelector = this.isExpanded
      ? "[data-progress-bar-large]"
      : "[data-progress-bar-small]";
    const progressBar = document.querySelector(progressBarSelector);

    if (progressBar && swiper.slides && swiper.slides.length > 0) {
      const progress = ((swiper.realIndex + 1) / swiper.slides.length) * 100;
      progressBar.style.width = `${progress}%`;
    }
  }

  getFilteredMedia(slides, currentVariant) {
    const fragment = new DocumentFragment();
    let currentMediaSize = 0;
    slides.forEach((slide) => {
      const media = slide.querySelector("img") || slide.querySelector("video");
      const itemAlt = media.alt;
      const correctImage =
        itemAlt &&
        currentVariant.selectedOptions.every((item) => {
          const trimedValue = item.value.replace(/ /g, "").toLowerCase();
          const altWithoutSpaces = itemAlt
            .replaceAll("/", "")
            .replaceAll(" ", "")
            .toLowerCase();
          return altWithoutSpaces.includes(trimedValue);
        });
      if (correctImage) {
        fragment.appendChild(slide);
        currentMediaSize += 1;
      }
    });

    // if product has only one variant and alt attributes doesnt have correct options
    if (currentMediaSize == 0) {
      slides.forEach((slide) => fragment.appendChild(slide));
      currentMediaSize = slides.length;
    }

    return { fragment, currentMediaSize };
  }

  replaceChildren(parent, newElements) {
    const wrapper = parent.querySelector(".swiper-wrapper");
    wrapper.replaceChildren(newElements);
  }

  reinitThumbSlider(currentVariant) {
    const clonedTemplate = new DocumentFragment();
    clonedTemplate.append(this.templateThumbs.content.cloneNode(true));

    const thumbAllSlides = [...clonedTemplate.children];
    const { fragment, currentMediaSize } = this.getFilteredMedia(
      thumbAllSlides,
      currentVariant
    );

    this.replaceChildren(this.thumbsSlider, fragment);
    this.thumbsSwiperInstance?.destroy(true, true);
    this.createThumbSlider(currentMediaSize);
  }

  reinitMainSlider(currentVariant) {
    const clonedTemplate = new DocumentFragment();
    clonedTemplate.append(this.templateMain.content.cloneNode(true));

    const mainAllSlides = [...clonedTemplate.children];
    const { fragment, currentMediaSize } = this.getFilteredMedia(
      mainAllSlides,
      currentVariant
    );
    this.replaceChildren(this.mainSlider, fragment);

    this.mainSwiperInstance?.destroy(true, true);
    this.createMainSlider(currentMediaSize);
  }

  reinit(currentVariant, isBundle = false) {
    if (!currentVariant || isBundle) return;
    this.reinitThumbSlider(currentVariant);
    this.reinitMainSlider(currentVariant);
  }

  init() {
    this.createThumbSlider();
    this.createMainSlider();
  }
}
